import { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import NaverMapView from "@/components/views/NaverMapView"
import "@/styles/AddressSearchPage.css"

type SelectedAddress = {
  address: string
  latitude: number
  longitude: number
}

const AddressSearchPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [mapInstance, setMapInstance] = useState<any>(null)

  const [selectedInfo, setSelectedInfo] = useState<SelectedAddress | null>(null)
  const [isGeocoding, setIsGeocoding] = useState(false)
  const isGeocodingRef = useRef(false)

  const initialCenter = useMemo(() => {
    const lat = parseFloat(searchParams.get("lat") || "37.5665")
    const lng = parseFloat(searchParams.get("lng") || "126.9780")
    return { lat, lng }
  }, [searchParams])

  const reverseGeocode = useCallback((coords: any) => {
    isGeocodingRef.current = true
    setIsGeocoding(true)
    window.naver.maps.Service.reverseGeocode(
      {
        coords: coords,
        orders: [
          window.naver.maps.Service.OrderType.ADDR,
          window.naver.maps.Service.OrderType.ROAD_ADDR,
        ].join(","),
      },
      (status: any, response: any) => {
        if (status === window.naver.maps.Service.Status.OK) {
          const result = response.v2
          const address =
            result.address.jibunAddress || result.address.roadAddress
          setSelectedInfo({
            address: address,
            latitude: coords.y,
            longitude: coords.x,
          })
        }
        setIsGeocoding(false)
        isGeocodingRef.current = false
      },
    )
  }, [])

  useEffect(() => {
    if (!mapInstance) return

    // 최초 한 번, 지도 로딩이 완료되면 초기 역지오코딩 수행
    window.naver.maps.Event.once(mapInstance, "idle", () => {
      console.log("Map is idle for the first time. Performing initial geocode.")
      reverseGeocode(mapInstance.getCenter())
    })

    // 드래그가 끝날 때마다 역지오코딩 수행
    const listener = window.naver.maps.Event.addListener(
      mapInstance,
      "dragend",
      () => {
        console.log("Drag ended. Performing geocode.")
        if (!isGeocodingRef.current) {
          reverseGeocode(mapInstance.getCenter())
        }
      },
    )

    return () => {
      window.naver.maps.Event.removeListener(listener)
    }
  }, [mapInstance, reverseGeocode])

  const handleConfirm = () => {
    if (selectedInfo && window.opener) {
      window.opener.postMessage(
        {
          type: "address-selected",
          data: selectedInfo,
        },
        window.location.origin,
      )
      window.close()
    }
  }

  return (
    <div className="address-search-page">
      <h2>지도 중앙의 위치로 주소 선택</h2>
      <p>지도를 움직여 중앙의 마커에 주소를 맞추세요.</p>
      <div className="map-container">
        <NaverMapView
          initialCenter={initialCenter}
          onMapLoad={setMapInstance}
        />
        <div className="center-marker"></div>
      </div>
      <div className="info-panel">
        {selectedInfo ? (
          <p>
            <strong>선택된 주소:</strong> {selectedInfo.address} <br />
            <strong>좌표:</strong> {selectedInfo.latitude.toFixed(6)},{" "}
            {selectedInfo.longitude.toFixed(6)}
          </p>
        ) : (
          <p>주소를 찾고 있습니다...</p>
        )}
        <button onClick={handleConfirm} disabled={!selectedInfo || isGeocoding}>
          {isGeocoding ? "주소 찾는 중..." : "이 위치로 주소 설정"}
        </button>
      </div>
    </div>
  )
}

export default AddressSearchPage
